import * as THREE from 'three';

export const createFrameConveyor = (
    scene: THREE.Scene,
    materials: any,
    componentsRef: React.MutableRefObject<Array<{
        mesh: THREE.Mesh;
        solidMaterial: THREE.Material;
        wireframeMaterial: THREE.Material;
    }>>,
    framePartsRef: React.MutableRefObject<Array<{
        mesh: THREE.Mesh;
        isCracked: boolean;
        offset: number;
    }>>
) => {
    const frameConveyorGroup = new THREE.Group();
    frameConveyorGroup.position.set(0, 4, -5);

    const frameThickness = 0.3;
    const frameLengthX = 8;
    const frameLengthZ = 4;

    // 顶部横梁 (X方向)
    const topX1 = new THREE.Mesh(new THREE.BoxGeometry(frameLengthX + frameThickness, frameThickness, frameThickness), materials.frameGray.clone());
    topX1.position.set(0, frameLengthZ / 2, frameLengthZ / 2);
    frameConveyorGroup.add(topX1);
    componentsRef.current.push({ mesh: topX1, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    const topX2 = new THREE.Mesh(new THREE.BoxGeometry(frameLengthX + frameThickness, frameThickness, frameThickness), materials.frameGray.clone());
    topX2.position.set(0, frameLengthZ / 2, -frameLengthZ / 2);
    frameConveyorGroup.add(topX2);
    componentsRef.current.push({ mesh: topX2, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    // 侧面横梁 (Z方向)
    const sideZ1 = new THREE.Mesh(new THREE.BoxGeometry(frameThickness, frameThickness, frameLengthZ + frameThickness), materials.frameGray.clone());
    sideZ1.position.set(frameLengthX / 2, frameLengthZ / 2, 0);
    frameConveyorGroup.add(sideZ1);
    componentsRef.current.push({ mesh: sideZ1, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    const sideZ2 = new THREE.Mesh(new THREE.BoxGeometry(frameThickness, frameThickness, frameLengthZ + frameThickness), materials.frameGray.clone());
    sideZ2.position.set(-frameLengthX / 2, frameLengthZ / 2, 0);
    frameConveyorGroup.add(sideZ2);
    componentsRef.current.push({ mesh: sideZ2, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    // 垂直支撑 (四角)
    const verticalSupportGeometry = new THREE.BoxGeometry(frameThickness, frameLengthZ + frameThickness, frameThickness);
    const v1 = new THREE.Mesh(verticalSupportGeometry, materials.frameGray.clone());
    v1.position.set(frameLengthX / 2, 0, frameLengthZ / 2);
    frameConveyorGroup.add(v1);
    componentsRef.current.push({ mesh: v1, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    const v2 = new THREE.Mesh(verticalSupportGeometry, materials.frameGray.clone());
    v2.position.set(-frameLengthX / 2, 0, frameLengthZ / 2);
    frameConveyorGroup.add(v2);
    componentsRef.current.push({ mesh: v2, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });
    
    const v3 = new THREE.Mesh(verticalSupportGeometry, materials.frameGray.clone());
    v3.position.set(frameLengthX / 2, 0, -frameLengthZ / 2);
    frameConveyorGroup.add(v3);
    componentsRef.current.push({ mesh: v3, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    const v4 = new THREE.Mesh(verticalSupportGeometry, materials.frameGray.clone());
    v4.position.set(-frameLengthX / 2, 0, -frameLengthZ / 2);
    frameConveyorGroup.add(v4);
    componentsRef.current.push({ mesh: v4, solidMaterial: materials.frameGray.clone(), wireframeMaterial: materials.wireframeCyan.clone() });

    scene.add(frameConveyorGroup);

    // 循环运动的多个零件
    const partGeometry = new THREE.BoxGeometry(1, 0.5, 1);
    const partHeightOffset = frameThickness + frameThickness / 2;
    const numParts = 9;

    const getPosition = (progress: number) => {
        const halfX = frameLengthX / 2;
        const halfZ = frameLengthZ / 2;
        let x = 0;
        let z = 0;
        if (progress < 0.25) {
            x = -halfX + (progress / 0.25) * frameLengthX;
            z = halfZ;
        } else if (progress < 0.5) {
            x = halfX;
            z = halfZ - ((progress - 0.25) / 0.25) * frameLengthZ;
        } else if (progress < 0.75) {
            x = halfX - ((progress - 0.5) / 0.25) * frameLengthX;
            z = -halfZ;
        } else {
            x = -halfX;
            z = -halfZ + ((progress - 0.75) / 0.25) * frameLengthZ;
        }
        return { x, y: halfZ + partHeightOffset, z };
    };

    for (let i = 0; i < numParts; i++) {
        const offset = i / numParts;
        const mesh = new THREE.Mesh(partGeometry, materials.partOrange.clone());
        const pos = getPosition(offset);
        mesh.position.set(pos.x, pos.y, pos.z);
        frameConveyorGroup.add(mesh);
        componentsRef.current.push({
            mesh,
            solidMaterial: materials.partOrange.clone(),
            wireframeMaterial: materials.wireframeCyan.clone()
        });

        const isCracked = i % 3 === 0;
        if (isCracked) {
            const crackMat = new THREE.LineBasicMaterial({ color: 0xff0000 });
            const points = [
                new THREE.Vector3(-0.5, 0.26, 0),
                new THREE.Vector3(0.5, 0.26, 0),
                new THREE.Vector3(0, 0.26, -0.5),
                new THREE.Vector3(0, 0.26, 0.5)
            ];
            const crackGeo = new THREE.BufferGeometry().setFromPoints(points);
            const crackLines = new THREE.LineSegments(crackGeo, crackMat);
            mesh.add(crackLines);
        }

        framePartsRef.current.push({ mesh, isCracked, offset });
    }
};
